import type { StatusTone } from "./statusTone";

export type PipelineStage =
  "new" | "contacted" | "sent" | "negotiating" | "closed" | "delivered" | "lost";

/**
 * Label + tom + classes de cor por estágio. Compartilhado por funil, lista,
 * página do lead e dashboard de vendas. Só tokens semânticos (ver
 * docs/specs/design-system/tokens.md) — nunca hex literal.
 */
export const STAGE_META: Record<
  PipelineStage,
  { label: string; tone: StatusTone; dot: string; pill: string }
> = {
  new: { label: "Novo", tone: "primary", dot: "bg-primary", pill: "bg-accent text-primary" },
  contacted: { label: "Em contato", tone: "info", dot: "bg-info", pill: "bg-info-soft text-info" },
  sent: { label: "Proposta enviada", tone: "warn", dot: "bg-warn", pill: "bg-warn-soft text-warn" },
  negotiating: {
    label: "Negociando",
    tone: "notice",
    dot: "bg-notice",
    pill: "bg-notice-soft text-notice",
  },
  closed: { label: "Fechado", tone: "ok", dot: "bg-ok", pill: "bg-ok-soft text-ok" },
  delivered: { label: "Entregue", tone: "ok", dot: "bg-ok", pill: "bg-ok-soft text-ok" },
  lost: { label: "Perdido", tone: "bad", dot: "bg-bad", pill: "bg-bad-soft text-bad" },
};

/** The five funnel columns. `delivered` and `lost` are terminal and only show up in the list view. */
export const FUNNEL_STAGES: PipelineStage[] = ["new", "contacted", "sent", "negotiating", "closed"];

export interface StageableLead {
  id: string;
  status: string;
  contacted_at: string | null;
}

export interface StageableBudget {
  lead_id: string | null;
  status: string;
}

export interface StageableContract {
  lead_id: string | null;
  status: string;
}

/**
 * A lead's pipeline stage is derived, never stored: the row's own `status`
 * only wins for the terminal/manual states (lost, delivered, converted,
 * contacted/qualified). Everything else comes from what actually happened to
 * its budgets and contracts, so the funnel can't drift from the documents.
 *
 * Order matters — later rules never override an earlier match.
 */
export function deriveStage(
  lead: StageableLead,
  budgets: StageableBudget[],
  contracts: StageableContract[] = [],
): PipelineStage {
  if (lead.status === "lost") return "lost";
  if (lead.status === "delivered") return "delivered";

  const lb = budgets.filter((b) => b.lead_id === lead.id);
  const lc = contracts.filter((c) => c.lead_id === lead.id);

  if (
    lead.status === "converted" ||
    lc.some((c) => c.status === "signed") ||
    lb.some((b) => b.status === "signed" || b.status === "accepted")
  ) {
    return "closed";
  }
  // "Negociando" = the contract is in the client's hands. A budget that is
  // partially_signed is still just "enviada" from the operator's point of
  // view: send-budget-signature pre-signs it with the admin's own signature,
  // so every sent proposal is partially_signed the moment it goes out.
  if (lc.some((c) => c.status === "sent" || c.status === "partially_signed")) return "negotiating";
  if (lb.some((b) => b.status === "sent" || b.status === "partially_signed")) return "sent";
  if (lead.contacted_at || lead.status === "contacted" || lead.status === "qualified")
    return "contacted";
  return "new";
}

// ── Primeiro contato (flows/lead-intake.md R4) ───────────────────────────────

export type ContactStamp = { contacted_at: string; status?: "contacted" };

/**
 * O que `useAddInteraction` grava no lead depois de registrar uma interação.
 * Só WhatsApp/ligação/e-mail contam como contato (nota não). Carimba
 * `contacted_at` uma única vez e só promove `new → contacted`; outros
 * estágios ficam como estão. `null` = nada a gravar.
 */
export function contactPatchForInteraction(
  type: string,
  current: { status: string; contacted_at: string | null } | null,
  nowIso: string,
): ContactStamp | null {
  if (type === "note") return null;
  if (!current || current.contacted_at) return null;
  return current.status === "new"
    ? { contacted_at: nowIso, status: "contacted" }
    : { contacted_at: nowIso };
}

// ── Valor do negócio e somas do funil (flows/lead-intake.md R9) ──────────────

/** Estados de proposta que contam como valor do negócio (rascunho e recusada não). */
export const VALUED_BUDGET_STATUSES: ReadonlySet<string> = new Set([
  "sent",
  "partially_signed",
  "signed",
  "accepted",
]);

export interface ValuableBudget {
  lead_id: string | null;
  status: string;
  net_total: number | null;
  barter_percent?: number | null;
  created_at: string;
}

export interface LeadDeal {
  value: number;
  barterPercent: number;
}

/**
 * Valor do negócio por lead = a proposta **mais recente** enviada/fechada.
 * Somar todas as versões contava revisões em dobro e inflava "em negociação"
 * com rascunhos que ninguém viu.
 */
export function leadDealValues(budgets: ValuableBudget[]): Record<string, LeadDeal> {
  const latest: Record<string, { at: number; deal: LeadDeal }> = {};
  for (const b of budgets) {
    if (!b.lead_id || !VALUED_BUDGET_STATUSES.has(b.status)) continue;
    const at = new Date(b.created_at).getTime();
    if (!latest[b.lead_id] || at > latest[b.lead_id].at) {
      latest[b.lead_id] = {
        at,
        deal: { value: b.net_total || 0, barterPercent: b.barter_percent ?? 0 },
      };
    }
  }
  return Object.fromEntries(Object.entries(latest).map(([id, v]) => [id, v.deal]));
}

export const EMPTY_STAGE_RECORD = (): Record<PipelineStage, number> => ({
  new: 0,
  contacted: 0,
  sent: 0,
  negotiating: 0,
  closed: 0,
  delivered: 0,
  lost: 0,
});

/** Soma do valor dos leads de cada coluna do funil. */
export function stageValueSums(
  grouped: Record<PipelineStage, { id: string }[]>,
  deals: Record<string, LeadDeal>,
): Record<PipelineStage, number> {
  const sums = EMPTY_STAGE_RECORD();
  for (const stage of Object.keys(grouped) as PipelineStage[]) {
    sums[stage] = grouped[stage].reduce((sum, l) => sum + (deals[l.id]?.value ?? 0), 0);
  }
  return sums;
}

export interface PipelineHeaderCounts {
  lost: number;
  delivered: number;
  open: number;
  /** Valor das propostas em mãos do cliente (estágios `sent` e `negotiating`). */
  openValue: number;
}

/** Contadores do cabeçalho do funil — leem o livro inteiro, não o filtro/busca. */
export function pipelineHeaderCounts(
  leads: { id: string }[],
  stageOf: (leadId: string) => PipelineStage | undefined,
  deals: Record<string, LeadDeal>,
): PipelineHeaderCounts {
  const c: PipelineHeaderCounts = { lost: 0, delivered: 0, open: 0, openValue: 0 };
  for (const l of leads) {
    const s = stageOf(l.id);
    if (s === "lost") c.lost++;
    else if (s === "delivered") c.delivered++;
    else if (s !== "closed") {
      c.open++;
      if (s === "sent" || s === "negotiating") c.openValue += deals[l.id]?.value ?? 0;
    }
  }
  return c;
}

// ── Parâmetros de URL do funil (flows/lead-intake.md R10) ────────────────────

const ALL_STAGES: ReadonlySet<string> = new Set<PipelineStage>([
  ...FUNNEL_STAGES,
  "delivered",
  "lost",
]);

export type PipelineView = "funnel" | "table";

/** Estágios que não têm coluna no funil — filtrar por eles só faz sentido na tabela. */
const TERMINAL_STAGES: ReadonlySet<PipelineStage> = new Set<PipelineStage>(["delivered", "lost"]);

export interface PipelineParams {
  /** `?stage=` válido (inclui os terminais `delivered`/`lost`); valor desconhecido = sem filtro. */
  stage: PipelineStage | null;
  /** `?new=1` abre o diálogo de criação rápida. */
  openNew: boolean;
  /**
   * `?view=table|funnel`. Sem `view`, filtro de estágio abre na tabela e sem
   * filtro, no funil. Estágio terminal sempre abre na tabela (o funil não tem
   * coluna para ele).
   */
  view: PipelineView;
}

export function parsePipelineParams(params: URLSearchParams): PipelineParams {
  const raw = params.get("stage");
  const stage = raw && ALL_STAGES.has(raw) ? (raw as PipelineStage) : null;
  const rawView = params.get("view");
  const asked: PipelineView | null = rawView === "table" || rawView === "funnel" ? rawView : null;
  const view: PipelineView =
    stage && TERMINAL_STAGES.has(stage) ? "table" : (asked ?? (stage ? "table" : "funnel"));
  return { stage, openNew: params.get("new") === "1", view };
}

/** Posição do estágio na ordem do funil (terminais no fim) — ordenação da tabela. */
export const STAGE_ORDER: Record<PipelineStage, number> = {
  new: 0,
  contacted: 1,
  sent: 2,
  negotiating: 3,
  closed: 4,
  delivered: 5,
  lost: 6,
};
